import { isWeb } from "@memorang/helpers/platform";
import { Image } from "expo-image";
import { LinearGradient } from "expo-linear-gradient";
import type { LayoutChangeEvent } from "react-native";
import { StyleSheet, useWindowDimensions } from "react-native";
import Animated from "react-native-reanimated";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import Container from "../Container";
import { supabaseImageUrlParser } from "../CustomImage";
import { type ScreenHeaderAction, ScreenHeaderBar } from "../ScreenHeaderBar";
import { AnimatedHeaderTitle } from "./AnimatedHeaderTitle";
import { COVER_OVERLAY_COLORS } from "./constants";
import { useAnimatedHeaderScroll } from "./useAnimatedHeaderScroll";

type Props = {
	title: string;
	coverImage?: string;
	leadingAction: ScreenHeaderAction;
	actions?: ScreenHeaderAction[];
	trailingContent?: React.ReactNode;
	renderHero: (ctx: {
		invertColors: boolean;
		onTitleLayout: (e: LayoutChangeEvent) => void;
		titleVariant: "headlineMedium" | "displaySmall";
	}) => React.ReactNode;
	children: React.ReactNode;
	contentPaddingBottom?: number;
	maxWidth?: "xxs" | "xs" | "sm" | "md" | "lg";
	bottomBar?: React.ReactNode;
};

export const AnimatedHeaderScreen = ({
	title,
	coverImage,
	leadingAction,
	actions,
	trailingContent,
	renderHero,
	children,
	contentPaddingBottom = 24,
	maxWidth = "sm",
	bottomBar,
}: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { width: screenWidth } = useWindowDimensions();
	const heroPaddingTop = isMobile ? 16 : 32;
	const titleVariant = isMobile ? "headlineMedium" : "displaySmall";
	const {
		scrollY,
		scrollHandler,
		headerHeight,
		titleScrollThreshold,
		isHeaderSolid,
		onHeaderLayout,
		onTitleTextLayout,
	} = useAnimatedHeaderScroll();

	const hasCoverImage = !!coverImage;
	const isHeaderTransparent = hasCoverImage && !isHeaderSolid;

	const optimizedCoverImageUri = hasCoverImage
		? supabaseImageUrlParser({ src: coverImage, width: screenWidth })
		: undefined;

	const headerBar = (
		<ScreenHeaderBar
			transparent={isHeaderTransparent}
			leadingAction={leadingAction}
			actions={actions}
			trailingContent={trailingContent}
			titleContent={
				<AnimatedHeaderTitle
					title={title}
					scrollY={scrollY}
					titleScrollThreshold={titleScrollThreshold}
					isTransparent={isHeaderTransparent}
					isScrollAware={hasCoverImage}
				/>
			}
		/>
	);

	return (
		<Box flex={1} height="100%" backgroundColor={theme.colors.background}>
			{!hasCoverImage && headerBar}
			<Animated.ScrollView
				onScroll={scrollHandler}
				scrollEventThrottle={16}
				showsVerticalScrollIndicator={isWeb}
				contentContainerStyle={{
					flexGrow: 1,
					paddingBottom: contentPaddingBottom,
				}}
			>
				{hasCoverImage ? (
					<Box position="relative" overflow="hidden">
						<Image
							source={{ uri: optimizedCoverImageUri }}
							style={StyleSheet.absoluteFill}
							contentFit="cover"
						/>
						<LinearGradient
							colors={COVER_OVERLAY_COLORS}
							style={StyleSheet.absoluteFill}
						/>
						<Container maxWidth={maxWidth} autoSize>
							<Box
								paddingTop={headerHeight + heroPaddingTop}
								paddingBottom={16}
							>
								{renderHero({
									invertColors: true,
									onTitleLayout: onTitleTextLayout,
									titleVariant,
								})}
							</Box>
						</Container>
					</Box>
				) : (
					<Container maxWidth={maxWidth} autoSize>
						<Box paddingTop={heroPaddingTop}>
							{renderHero({
								invertColors: false,
								onTitleLayout: onTitleTextLayout,
								titleVariant,
							})}
						</Box>
					</Container>
				)}
				<Container maxWidth={maxWidth}>{children}</Container>
			</Animated.ScrollView>
			{hasCoverImage && (
				<Box
					position="absolute"
					top={0}
					left={0}
					right={0}
					onLayout={onHeaderLayout}
				>
					{headerBar}
				</Box>
			)}
			{bottomBar}
		</Box>
	);
};
